import PropTypes from 'prop-types'

function EducationCard({ institution, onClick }) {
  const getLevelBadgeClass = type => {
    switch (type) {
      case 'Primary':
        return 'facility-badge bg-blue-100 text-blue-700 border-blue-200'
      case 'Secondary':
        return 'facility-badge bg-green-100 text-green-700 border-green-200'
      default:
        return 'facility-badge bg-surface-100 text-surface-700 border-surface-200'
    }
  }

  // Format numbers for display
  const formatNumber = num => {
    if (!num || num === 0) return '0'
    return Number(num).toLocaleString()
  }

  // Check if institution has meaningful data
  const hasEnrollmentData =
    institution.total_learners && institution.total_learners > 0
  const hasTeacherData =
    institution.total_teachers && institution.total_teachers > 0
  const hasInfrastructureData =
    institution.permanent_classrooms || institution.total_toilets

  return (
    <div className="card card-interactive group animate-fade-in p-3">
      {/* Institution Header */}
      <div className="mb-4">
        <h3 className="font-semibold text-surface-900 text-lg leading-tight group-hover:text-primary-700 transition-colors capitalize">
          {institution.institution_name || institution.name}
        </h3>
        <div className="flex items-center justify-between mt-3">
          <span className={getLevelBadgeClass(institution.institution_type)}>
            {institution.institution_type}
          </span>
          <span className="text-xs text-surface-500 font-medium">
            {institution.ownership}
          </span>
        </div>
      </div>

      {/* Location */}
      <div className="mb-4">
        <div className="flex items-center text-sm text-surface-600">
          <span className="text-primary-500 mr-1">📍</span>
          <span className="font-medium">{institution.subcounty}</span>
        </div>
        <div className="text-xs text-surface-500 mt-1 ml-6">
          {institution.district} District
        </div>
        {institution.parish && (
          <div className="text-xs text-surface-400 mt-1 ml-6">
            {institution.parish} Parish
          </div>
        )}
      </div>

      {/* Key Stats - Only show if data is available */}
      {(hasEnrollmentData || hasTeacherData) && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          {hasEnrollmentData && (
            <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3">
              <div className="text-xl font-bold text-blue-600">
                {formatNumber(institution.total_learners)}
              </div>
              <div className="text-xs text-blue-700 font-medium">Students</div>
            </div>
          )}
          {hasTeacherData && (
            <div className="text-center bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3">
              <div className="text-xl font-bold text-green-600">
                {formatNumber(institution.total_teachers)}
              </div>
              <div className="text-xs text-green-700 font-medium">Teachers</div>
            </div>
          )}
        </div>
      )}

      {/* Student Demographics - Only show if enrollment data exists */}
      {hasEnrollmentData &&
        institution.boys_count &&
        institution.girls_count && (
          <div className="mb-4 pb-4 border-b border-surface-100">
            <div className="text-xs text-surface-500 font-medium mb-2">
              Student Demographics:
            </div>
            <div className="flex justify-between text-sm">
              <div className="text-center">
                <div className="font-semibold text-blue-600">
                  {formatNumber(institution.boys_count)}
                </div>
                <div className="text-xs text-surface-500">Boys</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-pink-600">
                  {formatNumber(institution.girls_count)}
                </div>
                <div className="text-xs text-surface-500">Girls</div>
              </div>
              {institution.disabled_learners > 0 && (
                <div className="text-center">
                  <div className="font-semibold text-purple-600">
                    {formatNumber(institution.disabled_learners)}
                  </div>
                  <div className="text-xs text-surface-500">Special Needs</div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Key Facilities */}
      <div className="mb-5">
        <div className="text-xs text-surface-500 font-medium mb-2">
          Key Facilities:
        </div>
        <div className="flex flex-wrap gap-2">
          {institution.permanent_classrooms > 0 && (
            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
              🏫 {institution.permanent_classrooms} Classrooms
            </span>
          )}
          {institution.electricity_available === 'Yes' && (
            <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 rounded-md text-xs font-medium">
              ⚡ Electricity
            </span>
          )}
          {institution.water_available === 'Yes' && (
            <span className="inline-flex items-center px-2 py-1 bg-cyan-100 text-cyan-700 rounded-md text-xs font-medium">
              💧 Water
            </span>
          )}
          {institution.ict_lab_available === 'Yes' && (
            <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium">
              💻 ICT Lab
            </span>
          )}
          {!hasInfrastructureData && (
            <span className="inline-flex items-center px-2 py-1 bg-surface-100 text-surface-600 rounded-md text-xs font-medium">
              📊 Limited data
            </span>
          )}
        </div>
      </div>

      {/* Key Ratios - Only show if meaningful data exists */}
      {hasEnrollmentData &&
        hasTeacherData &&
        institution.pupil_teacher_ratio && (
          <div className="mb-4 pb-4 border-b border-surface-100">
            <div className="text-xs text-surface-500 font-medium mb-2">
              Key Ratios:
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-medium text-surface-700">
                  Student:Teacher
                </span>
                <div className="text-lg font-bold text-primary-600">
                  {Math.round(institution.pupil_teacher_ratio)}:1
                </div>
              </div>
              {institution.pupil_classroom_ratio && (
                <div>
                  <span className="font-medium text-surface-700">
                    Student:Classroom
                  </span>
                  <div className="text-lg font-bold text-primary-600">
                    {Math.round(institution.pupil_classroom_ratio)}:1
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* View Details Button */}
      <button
        onClick={() => onClick?.(institution)}
        className="btn btn-primary w-full group-hover:bg-primary-600 transform group-hover:scale-[1.02] transition-all duration-200"
      >
        <span className="text-sm font-semibold">View Details</span>
      </button>
    </div>
  )
}

EducationCard.propTypes = {
  institution: PropTypes.shape({
    institution_name: PropTypes.string,
    name: PropTypes.string,
    institution_type: PropTypes.string,
    ownership: PropTypes.string,
    subcounty: PropTypes.string,
    district: PropTypes.string,
    parish: PropTypes.string,
    total_learners: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    boys_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    girls_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled_learners: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    total_teachers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    permanent_classrooms: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    total_toilets: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    electricity_available: PropTypes.string,
    water_available: PropTypes.string,
    ict_lab_available: PropTypes.string,
    pupil_teacher_ratio: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    pupil_classroom_ratio: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }).isRequired,
  onClick: PropTypes.func,
}

export default EducationCard
